import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
  Switch,
  IconButton,
  Tooltip,
  Paper,
  Link,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material'
import IconPicker, { usePresentationIcons } from '../presentation/IconPicker'
import { presentationAPI, resourcesAPI } from '../../services/api'

// Mirrors data-service src/presentation/schema.js (resource category limits).
const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]*$/
const SLUG_MAX = 40
const TITLE_MAX = 80
const DEFAULT_ICON = 'compass'

let nextKey = 1

/** Editable rows from the stored presentation.resource_categories array. */
function toRows(presentation) {
  const stored = presentation && Array.isArray(presentation.resource_categories)
    ? presentation.resource_categories
    : []
  return stored.map((c) => ({
    key: `row-${nextKey++}`,
    id: c.id || '',
    title: c.title || '',
    description: c.description || '',
    icon: c.icon || null,
    themes: Array.isArray(c.themes) ? c.themes : [],
    query: c.query || '',
    enabled: c.enabled !== false,
    // Slugs are immutable once stored: the chat seed carries them.
    existing: Boolean(c.id),
  }))
}

function newRow() {
  return {
    key: `row-${nextKey++}`,
    id: '',
    title: '',
    description: '',
    icon: null,
    themes: [],
    query: '',
    enabled: true,
    existing: false,
  }
}

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, SLUG_MAX)
}

/** Client-side validation matching the server rules; keyed by row key. */
function validateRows(rows, limits) {
  const errors = {}
  const seen = new Map()
  rows.forEach((row) => {
    const e = {}
    const id = row.id.trim()
    if (!id) e.id = 'Required'
    else if (id.length > SLUG_MAX || !SLUG_PATTERN.test(id)) {
      e.id = `Lowercase a-z, 0-9, _ or -, up to ${SLUG_MAX} characters`
    } else if (seen.has(id)) {
      e.id = 'Duplicate id'
      errors[seen.get(id)] = { ...(errors[seen.get(id)] || {}), id: 'Duplicate id' }
    } else {
      seen.set(id, row.key)
    }
    if (!row.title.trim()) e.title = 'Required'
    else if (row.title.length > TITLE_MAX) e.title = `At most ${TITLE_MAX} characters`
    if (row.description.length > limits.description) {
      e.description = `At most ${limits.description} characters`
    }
    if (!row.icon) e.icon = 'Choose an icon'
    if (Object.keys(e).length) errors[row.key] = { ...(errors[row.key] || {}), ...e }
  })
  return errors
}

/** Stored shape for the rows (schema.js ResourceCategory). */
function toCategories(rows) {
  return rows.map((row) => {
    const category = {
      id: row.id.trim(),
      title: row.title.trim(),
      icon: row.icon,
      themes: row.themes.map((t) => t.trim()).filter(Boolean),
      query: row.query.trim() || null,
      enabled: row.enabled,
    }
    const description = row.description.trim()
    if (description) category.description = description
    return category
  })
}

/**
 * Map a data-service "Invalid presentation: presentation.resource_categories[2].icon ..."
 * message to { rowIndex: { field: message } }. Returns {} for other messages.
 */
function parseCategoryErrors(message) {
  const errors = {}
  if (typeof message !== 'string') return errors
  const pattern = /presentation\.resource_categories\[(\d+)\]\.(\w+)([^,]*)/g
  let match
  while ((match = pattern.exec(message)) !== null) {
    const [, index, field, rest] = match
    errors[index] = { ...(errors[index] || {}), [field]: `${field}${rest}`.trim() }
  }
  return errors
}

/**
 * Edits presentation.resource_categories on the intervention's
 * resource_finder pathway row: the categories the app's resources picker
 * shows, each mapped to resource themes (and an optional free-text
 * query) that seed the chat search.
 *
 * `onSave(presentation)` receives the pathway's stored presentation with
 * the full category array replaced and every other key preserved; it
 * must return a promise, and a rejection is shown inline.
 */
function ResourceCategoriesDialog({ open, onClose, pathway, groupIds = [], onSave, saving }) {
  const { limits } = usePresentationIcons()
  const [rows, setRows] = useState([])
  const [themes, setThemes] = useState([])
  const [themesLoading, setThemesLoading] = useState(false)
  const [themesError, setThemesError] = useState(null)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [showErrors, setShowErrors] = useState(false)
  const [starterLoading, setStarterLoading] = useState(false)

  useEffect(() => {
    if (open && pathway) {
      setRows(toRows(pathway.presentation))
      setError(null)
      setFieldErrors({})
      setShowErrors(false)
    }
  }, [open, pathway])

  // Keyed on the joined ids: the parent builds a fresh array each render.
  const groupKey = groupIds.join(',')
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setThemesLoading(true)
    setThemesError(null)
    resourcesAPI
      .getThemes(groupKey ? groupKey.split(',').map(Number) : [])
      .then((data) => {
        if (!cancelled) setThemes(Array.isArray(data) ? data : [])
      })
      .catch((err) => {
        console.error('Error loading resource themes:', err)
        if (!cancelled) {
          setThemes([])
          setThemesError('Could not load resource themes; type theme names to add them.')
        }
      })
      .finally(() => {
        if (!cancelled) setThemesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, groupKey])

  const countByTheme = useMemo(
    () => Object.fromEntries(themes.map((t) => [t.theme, Number(t.count) || 0])),
    [themes],
  )
  const themeOptions = useMemo(() => themes.map((t) => t.theme), [themes])

  const clientErrors = useMemo(() => validateRows(rows, limits), [rows, limits])

  if (!pathway) return null

  const matchCount = (row) => row.themes.reduce((sum, t) => sum + (countByTheme[t] || 0), 0)

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }

  const moveRow = (index, delta) => {
    setRows((prev) => {
      const target = index + delta
      if (target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const [row] = next.splice(index, 1)
      next.splice(target, 0, row)
      return next
    })
  }

  const removeRow = (key) => setRows((prev) => prev.filter((row) => row.key !== key))

  const loadStarterSet = async () => {
    setStarterLoading(true)
    try {
      const categories = await presentationAPI.getStarterResourceCategories()
      setRows(toRows({ resource_categories: categories }))
      setError(null)
    } catch (err) {
      console.error('Error loading starter categories:', err)
      setError('Could not load the starter categories')
    } finally {
      setStarterLoading(false)
    }
  }

  const addRow = () => setRows((prev) => [...prev, newRow()])

  const rowErrors = (row, index) => ({
    ...(showErrors ? clientErrors[row.key] || {} : {}),
    ...(fieldErrors[index] || {}),
  })

  const handleSave = async () => {
    setError(null)
    setFieldErrors({})
    setShowErrors(true)
    if (Object.keys(clientErrors).length > 0) {
      setError('Fix the highlighted fields before saving.')
      return
    }
    try {
      const stored = pathway.presentation && typeof pathway.presentation === 'object'
        ? pathway.presentation
        : {}
      await onSave({ ...stored, resource_categories: toCategories(rows) })
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || err.message
      const parsed = parseCategoryErrors(message)
      setFieldErrors(parsed)
      setError(
        Object.keys(parsed).length > 0
          ? 'The server rejected some categories; see the highlighted fields.'
          : message || 'Failed to save resource categories',
      )
    }
  }

  const enabledCount = rows.filter((r) => r.enabled).length

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Resource Categories
        <Typography variant="body2" color="text.secondary">
          {pathway.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The categories patients pick from to find resources in this intervention. Each maps
          to resource themes (and optionally free-text keywords) that seed the conversation.
          Order here is display order; disabled categories stay configured but are hidden
          from the app.
        </Typography>

        {groupIds.length === 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            This pathway has no content groups configured, so match counts cover all
            resources rather than the ones this intervention can reach.
          </Alert>
        )}
        {themesError && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {themesError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {rows.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={loadStarterSet} disabled={starterLoading}>
                {starterLoading ? 'Loading…' : 'Load starter set'}
              </Button>
            }
          >
            No categories yet. The app hides the resources picker until at least one enabled
            category is saved. New resources pathways get the starter set automatically; load
            it here to restore it.
          </Alert>
        )}

        {rows.map((row, index) => {
          const errors = rowErrors(row, index)
          const count = matchCount(row)
          const hasThemes = row.themes.length > 0
          const zeroMatches = hasThemes && count === 0 && !themesLoading
          return (
            <Paper
              key={row.key}
              variant="outlined"
              sx={{ p: 1.5, mb: 1.5, opacity: row.enabled ? 1 : 0.7 }}
            >
              <Box display="flex" gap={1} alignItems="flex-start">
                <Box display="flex" flexDirection="column" mt={0.25}>
                  <IconButton
                    size="small"
                    onClick={() => moveRow(index, -1)}
                    disabled={saving || index === 0}
                    aria-label="Move up"
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveRow(index, 1)}
                    disabled={saving || index === rows.length - 1}
                    aria-label="Move down"
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Box flex={1} display="flex" flexDirection="column" gap={1.25} minWidth={0}>
                  <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                    <Box sx={{ pt: 0.25 }}>
                      <IconPicker
                        value={row.icon}
                        onChange={(icon) => updateRow(row.key, { icon })}
                        placeholder={DEFAULT_ICON}
                        disabled={saving}
                        error={errors.icon}
                      />
                    </Box>
                    <Tooltip
                      title={
                        row.existing
                          ? 'The id is fixed once saved: the app and chat seed refer to it.'
                          : 'Short stable id (slug). Cannot be changed after saving.'
                      }
                    >
                      <TextField
                        size="small"
                        label="Id"
                        value={row.id}
                        onChange={(e) => updateRow(row.key, { id: e.target.value })}
                        disabled={saving || row.existing}
                        error={Boolean(errors.id)}
                        helperText={errors.id}
                        inputProps={{ maxLength: SLUG_MAX, style: { fontFamily: 'monospace' } }}
                        sx={{ width: 160 }}
                      />
                    </Tooltip>
                    <TextField
                      size="small"
                      label="Title"
                      value={row.title}
                      onChange={(e) => {
                        const patch = { title: e.target.value }
                        if (!row.existing && (!row.id || row.id === slugify(row.title))) {
                          patch.id = slugify(e.target.value)
                        }
                        updateRow(row.key, patch)
                      }}
                      disabled={saving}
                      error={Boolean(errors.title)}
                      helperText={errors.title || `${row.title.length}/${TITLE_MAX}`}
                      inputProps={{ maxLength: TITLE_MAX }}
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <Tooltip title={row.enabled ? 'Shown in the app' : 'Hidden from the app'}>
                      <Box display="flex" alignItems="center" sx={{ pt: 0.25 }}>
                        <Switch
                          size="small"
                          checked={row.enabled}
                          onChange={(e) => updateRow(row.key, { enabled: e.target.checked })}
                          disabled={saving}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {row.enabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title="Remove category">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeRow(row.key)}
                          disabled={saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  <TextField
                    size="small"
                    label="Description"
                    value={row.description}
                    onChange={(e) => updateRow(row.key, { description: e.target.value })}
                    disabled={saving}
                    error={Boolean(errors.description)}
                    helperText={
                      errors.description || `${row.description.length}/${limits.description}`
                    }
                    inputProps={{ maxLength: limits.description }}
                    fullWidth
                  />

                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={themeOptions}
                      value={row.themes}
                      onChange={(_, value) =>
                        updateRow(row.key, {
                          themes: [...new Set(value.map((t) => String(t).trim()).filter(Boolean))],
                        })
                      }
                      loading={themesLoading}
                      disabled={saving}
                      renderOption={(props, option) => (
                        <li {...props} key={option}>
                          <Box display="flex" justifyContent="space-between" width="100%">
                            <span>{option}</span>
                            <Typography variant="caption" color="text.secondary">
                              {countByTheme[option] ?? 0}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, i) => {
                          const { key, ...tagProps } = getTagProps({ index: i })
                          const n = countByTheme[option]
                          return (
                            <Chip
                              key={key}
                              size="small"
                              label={`${option} (${n ?? 0})`}
                              color={n ? 'default' : 'warning'}
                              variant={n ? 'filled' : 'outlined'}
                              {...tagProps}
                            />
                          )
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Themes"
                          placeholder={row.themes.length ? '' : 'Pick resource themes'}
                          error={Boolean(errors.themes)}
                          helperText={errors.themes}
                        />
                      )}
                      sx={{ flex: 2, minWidth: 260 }}
                    />
                    <TextField
                      size="small"
                      label="Query (optional)"
                      value={row.query}
                      onChange={(e) => updateRow(row.key, { query: e.target.value })}
                      disabled={saving}
                      placeholder="free-text keywords"
                      error={Boolean(errors.query)}
                      helperText={errors.query}
                      sx={{ flex: 1, minWidth: 180 }}
                    />
                  </Box>

                  <Typography
                    variant="caption"
                    color={zeroMatches && row.enabled ? 'warning.main' : 'text.secondary'}
                  >
                    {themesLoading && 'Counting matches…'}
                    {!themesLoading && !hasThemes && !row.query.trim() && (
                      'No themes or query: this category will seed an unfiltered search.'
                    )}
                    {!themesLoading && !hasThemes && row.query.trim() && (
                      'Free-text query only; matches are not counted here.'
                    )}
                    {!themesLoading && hasThemes && count > 0 && (
                      `${count} matching resource${count === 1 ? '' : 's'}`
                      + `${row.query.trim() ? ' by theme (query not counted)' : ''}`
                    )}
                    {zeroMatches && (
                      <>
                        No resources match these themes
                        {groupIds.length > 0 ? " in this pathway's content groups" : ''}.
                        {row.enabled && (
                          <>
                            {' '}Consider{' '}
                            <Link
                              component="button"
                              type="button"
                              variant="caption"
                              onClick={() => updateRow(row.key, { enabled: false })}
                              disabled={saving}
                            >
                              disabling it
                            </Link>
                            {' '}until resources are added.
                          </>
                        )}
                      </>
                    )}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )
        })}

        <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={saving}>
          Add category
        </Button>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
          {rows.length} categor{rows.length === 1 ? 'y' : 'ies'}, {enabledCount} enabled
        </Typography>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ResourceCategoriesDialog
